<template>
  <aside :style="asideStyle">
    <!-- header -->
    <div
      v-if="embedded && activeCount > 0"
      :style="{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '7px 12px 0' }"
    >
      <button :style="resetBtn" @click="search.setQuery('')">Reset all</button>
    </div>
    <header
      v-else-if="!embedded"
      :style="{ padding: '12px 14px', borderBottom: '1px solid var(--p-content-border-color)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }"
    >
      <span
        :style="{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '12px',
          fontWeight: 800,
          letterSpacing: '.04em',
          textTransform: 'uppercase',
          color: activeCount ? 'var(--p-primary-700)' : 'var(--p-text-muted-color)',
        }"
      >
        <i class="pi pi-sliders-h" :style="{ fontSize: '12px' }" /> Builder
        <Badge v-if="activeCount > 0" :n="activeCount" />
      </span>
      <button v-if="activeCount > 0" :style="resetBtn" @click="search.setQuery('')">Reset</button>
    </header>

    <!-- sections -->
    <div :style="{ flex: 1, minHeight: 0, overflowY: 'auto' }">
      <component
        :is="sectionFor(fields[k])"
        v-for="k in keys"
        :key="k"
        :field-key="k"
        :decl="fields[k]"
        :field-slot="slotFor(k)"
        :meta="metaFor(k)"
        :search="search"
        :facet-provider="facetProvider"
        :debounce-ms="debounceMs"
        :field-definitions="fieldDefinitions"
        :value-provider="valueProvider"
        :known-values="knownValues"
      />
    </div>

    <!-- advanced clauses — preserved, never silently dropped -->
    <div
      v-if="fieldView.advanced.length > 0"
      :style="{ borderTop: '1px solid var(--p-content-border-color)', padding: '10px 12px', background: 'color-mix(in srgb, var(--p-status-warning) 7%, transparent)' }"
    >
      <div
        :style="{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '10px', fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--p-text-muted-color)', marginBottom: '8px' }"
      >
        <i class="pi pi-code" :style="{ fontSize: '11px' }" /> Advanced clauses
      </div>
      <div :style="{ display: 'flex', flexDirection: 'column', gap: '6px' }">
        <div
          v-for="a in fieldView.advanced"
          :key="a.index"
          title="Not expressible as checkboxes — kept verbatim"
          :style="{ display: 'flex', alignItems: 'center', gap: '8px', padding: '5px 8px', borderRadius: '8px', background: 'var(--p-content-background)', border: '1px solid var(--p-content-border-color)' }"
        >
          <code :style="{ flex: 1, fontFamily: mono, fontSize: '11.5px', color: 'var(--p-text-color)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }">{{ a.text }}</code>
          <button
            title="Remove clause"
            :style="{ background: 'transparent', border: 0, padding: '2px', cursor: 'pointer', color: 'var(--p-surface-400)', display: 'flex' }"
            @click="search.removeConjunct(a.index)"
          >
            <i class="pi pi-times" :style="{ fontSize: '10px' }" />
          </button>
        </div>
      </div>
    </div>
  </aside>
</template>

<script setup>
// ── FQL SearchSidebar — checkbox + range builder ─────────────────────────
// Two-way synced with the bar via the single query string. One section per
// field, chosen by type. Advanced (unrepresentable) clauses are preserved as
// removable chips. Ported 1:1 from fql-search.jsx SearchSidebar.
import { computed } from 'vue'
import { mono, Badge } from './search/bits.js'
import FacetSection from './search/FacetSection.vue'
import BooleanSection from './search/BooleanSection.vue'
import DateRangeSection from './search/DateRangeSection.vue'
import RangeSection from './search/RangeSection.vue'
import ExactSection from './search/ExactSection.vue'
import StringSection from './search/StringSection.vue'

const props = defineProps({
  search: { type: Object, required: true },
  fieldDefinitions: { type: Object, required: true },
  fieldMeta: { type: Object, default: () => ({}) },
  valueProvider: { type: Function, default: null },
  facetProvider: { type: Function, default: null },
  knownValues: { type: Object, default: null },
  debounceMs: { type: Number, default: 220 },
  embedded: { type: Boolean, default: false },
})

const fields = computed(() => props.fieldDefinitions.fields || {})
// A field can opt out of the filter UI via fieldMeta[key].searchHidden — it
// stays in fieldDefinitions (for column display) but renders no filter section.
const keys = computed(() => Object.keys(fields.value).filter((k) => !props.fieldMeta[k]?.searchHidden))
const fieldView = computed(() => props.search.fieldView)
const activeCount = computed(
  () =>
    Object.values(fieldView.value.byField).reduce((n, s) => n + s.values.size + (s.range ? 1 : 0), 0) + fieldView.value.advanced.length,
)
const slotFor = (k) => fieldView.value.byField[k] || { values: new Set(), range: null }
const metaFor = (k) => props.fieldMeta[k] || {}

// Choose the section component for a field declaration.
function sectionFor(decl) {
  if (decl.type === 'enum') return FacetSection
  if (decl.type === 'boolean') return BooleanSection
  if (decl.type === 'date') return DateRangeSection
  if (decl.type === 'number') return RangeSection
  if (decl.type === 'objectId') return ExactSection
  if (decl.type === 'string' && decl.facet) return FacetSection
  return StringSection
}

const asideStyle = computed(() =>
  props.embedded
    ? { width: '100%', flex: 1, minHeight: 0, alignSelf: 'stretch', background: 'transparent', display: 'flex', flexDirection: 'column' }
    : {
        width: '268px',
        flexShrink: 0,
        alignSelf: 'stretch',
        background: 'var(--p-content-background)',
        border: '1px solid var(--p-content-border-color)',
        borderRadius: '14px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      },
)
const resetBtn = {
  background: 'transparent',
  border: 0,
  padding: 0,
  fontSize: '11px',
  fontWeight: 700,
  color: 'var(--p-status-info)',
  cursor: 'pointer',
  fontFamily: 'inherit',
}
</script>
